import { useEffect, useRef, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Typography } from "@material-tailwind/react";
import { ReactComponent as UserLogo } from "../../assets/icons/UserLogo.svg";
import { ReactComponent as MapPinLogo } from "../../assets/icons/MapPinLogo.svg";
import { ReactComponent as ViewLogo } from "../../assets/icons/ViewLogo.svg";
import { ReactComponent as EditIconLogo } from "../../assets/icons/EditIconLogo.svg";
import { ReactComponent as TrashLogo } from "../../assets/icons/TrashLogo.svg";
import { ReactComponent as SearchLogo } from "../../assets/icons/SearchLogo.svg";
import useStudentList from "../hooks/useStudentList";
import useDebounce from "../hooks/useDebounce";
import { setEditingStudent } from "../../store/studentManagementReducer";
import "../features/common/Style.css";

const TABLE_HEAD = [
  "Student",
  "Education",
  "Performance",
  "Actions",
];

const StudentTable = () => {
  const dispatch = useDispatch();
  const { students } = useSelector(
    (state) => state.studentManagement
  );
  const { loadMore, hasMore, loading, fetchStudentList } = useStudentList();
  const scrollRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const isInitialMount = useRef(true);

  useEffect(() => {
    fetchStudentList(1, false, {});
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const trimmed = debouncedSearchQuery.trim();

    const filters = trimmed
      ? trimmed.includes("@")
        ? { email: trimmed }
        : { name: trimmed }
      : {};

    fetchStudentList(1, false, filters);
  }, [debouncedSearchQuery]);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight - 10 && hasMore && !loading) {
      loadMore();
    }
  }, [hasMore, loading, loadMore]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleEditClick = (student) => {
    dispatch(setEditingStudent(student));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <div className="flex-1 relative">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <SearchLogo />
          </div>
          <input
            type="text"
            placeholder="Search students by name or email..."
            className="w-full pl-12 pr-4 py-2 border-[0.5px] border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div
        ref={scrollRef}
        className="overflow-x-auto max-h-[600px] overflow-y-auto custom-scrollbar pr-2"
      >
        <table className="w-full">
          <thead className="table-header-gradient sticky top-0">
            <tr>
              {TABLE_HEAD.map((head) => (
                <th
                  key={head}
                  className="px-6 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider"
                >
                  <Typography variant="small" color="blue-gray" className="font-bold">
                    {head}
                  </Typography>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {students && students.length > 0 ? (
              students.map((student) => {
                return (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <UserLogo />
                        </div>
                        <div className="ml-4">
                          <Typography
                            variant="small"
                            color="gray"
                            className="text-sm font-medium text-gray-900-medium"
                          >
                            {student?.name || "N/A"}
                          </Typography>
                          <Typography
                            variant="small"
                            className="text-sm text-gray-500"
                          >
                            {student.email}
                          </Typography>
                          {student.address && (
                            <div className="flex items-center gap-1 text-xs text-gray-400">
                              <MapPinLogo />
                              {student.address}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Education */}
                    <td className="px-6 py-4">
                      <Typography
                        variant="small"
                        className="font-medium text-gray-700"
                      >
                        {student.course || "N/A"}
                      </Typography>
                      <Typography variant="small" className="text-gray-500">
                        {student.university || "N/A"}
                      </Typography>
                      {student.graduation_year && (
                        <Typography
                          variant="small"
                          className="text-gray-500 text-xs"
                        >
                          Class of {student.graduation_year}
                        </Typography>
                      )}
                    </td>

                  <td className="px-6 py-4">
                    {student.avg_performance ?? "N/A"}
                  </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button 
                          className="text-green-600 hover:text-green-700"
                          onClick={() => handleEditClick(student)}
                        >
                          <EditIconLogo className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                  {loading ? "Loading students..." : "No students found"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {loading && students && students.length > 0 && (
          <div className="text-center py-4 text-gray-500 text-sm">
            Loading more students...
          </div>
        )}
        {!hasMore && students && students.length > 0 && (
          <div className="text-center py-4 text-gray-400 text-sm">
            No more students to load
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentTable;